import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Client from "./Client";
import Editor from "./Editor";
import { initSocket } from "../Socket";
import Chat from "./Chat";
import { ACTIONS } from "../Actions";
import {
  useNavigate,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "axios";

// List of supported languages
const LANGUAGES = [
  "python3",
  "java",
  "cpp",
  "nodejs",
  "c",
  "ruby",
  "go",
  "scala",
  "bash",
  "sql",
  "pascal",
  "csharp",
  "php",
  "swift",
  "rust",
  "r",
];

function EditorPage() {
  const [clients, setClients] = useState([]);
  const [output, setOutput] = useState("");
  const [isCompileWindowOpen, setIsCompileWindowOpen] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("python3");
  const [isSocketReady, setIsSocketReady] = useState(false);
  const [showBannedUsers, setShowBannedUsers] = useState(false);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [compilerBounds, setCompilerBounds] = useState({ left: 0, right: 0 });
  const codeRef = useRef("");
  const [ownerId, setOwnerId] = useState(null);
  const [myRole, setMyRole] = useState("read-only");
  const location = useLocation();
  const navigate = useNavigate();
  const { roomId } = useParams();
  const username = location.state?.username;
  const [isChatOpen, setIsChatOpen] = useState(false); // 🔥 CHAT STATE

  const socketRef = useRef(null);
  const editorPaneRef = useRef(null);
  const menuRef = useRef(null);
  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => {
    const handleErrors = (err) => {
      console.log("Error", err);
      toast.error("Socket connection failed, Try again later");
      navigate("/");
    };

    const init = async () => {
      const socket = await initSocket();
      socketRef.current = socket;
      setIsSocketReady(true);
      socket.on("connect_error", handleErrors);
      socket.on("connect_failed", handleErrors);

      socket.emit(ACTIONS.JOIN, {
        roomId,
        username,
      });

      socket.on(ACTIONS.JOIN_ERROR, ({ message }) => {
        toast.error(message || "Unable to join room");
        navigate("/");
      });

      socket.on(
        ACTIONS.JOINED,
        ({ clients, username: joinedUser, socketId, ownerId }) => {
          if (socketId === socketRef.current.id) {
            toast.success(ownerId === socketId ? "room created" : "room joined");
          } else {
            toast.success(`${joinedUser} joined the room.`);
          }

          setClients(clients);
          setOwnerId(ownerId);

          const me = clients.find((c) => c.socketId === socketRef.current.id);
          if (me) {
            setMyRole(me.role);
          }

          if (socketId !== socketRef.current.id) {
            socket.emit(ACTIONS.SYNC_CODE, {
              code: codeRef.current,
              socketId,
            });
          }
        }
      );

      socket.on(
        ACTIONS.DISCONNECTED,
        ({ socketId, username: leftUser, ownerId: nextOwnerId, clients, reason }) => {
          if (leftUser && leftUser !== username) {
            if (reason === "kicked") {
              toast(`${leftUser} was kicked from the room`);
            } else if (reason === "banned") {
              toast(`${leftUser} was banned from the room`);
            } else {
              toast.success(`${leftUser} left the room`);
            }
          }

          if (Array.isArray(clients)) {
            setClients(clients);
            const me = clients.find((c) => c.socketId === socketRef.current.id);
            setMyRole(me?.role || "read-only");
          } else {
            setClients((prev) =>
              prev.filter((client) => client.socketId !== socketId)
            );
          }

          if (typeof nextOwnerId !== "undefined") {
            setOwnerId(nextOwnerId);
          }
        }
      );

      socket.on(ACTIONS.ROLE_UPDATED, ({ targetId, role, ownerId, clients }) => {
        if (Array.isArray(clients)) {
          setClients(clients);
          const me = clients.find((c) => c.socketId === socketRef.current.id);
          setMyRole(me?.role || "read-only");
        } else {
          setClients((prev) =>
            prev.map((c) => (c.socketId === targetId ? { ...c, role } : c))
          );
          if (targetId === socketRef.current.id) {
            setMyRole(role);
          }
        }

        if (typeof ownerId !== "undefined") {
          setOwnerId(ownerId);
        }
      });

      socket.on(ACTIONS.KICKED, ({ message }) => {
        toast.error(message || "You were kicked from the room");
        navigate("/");
      });

      socket.on(ACTIONS.BANNED, ({ message }) => {
        toast.error(message || "You were banned from the room");
        navigate("/");
      });

      socket.on(ACTIONS.BANNED_USERS, ({ users }) => {
        setBannedUsers(Array.isArray(users) ? users : []);
      });
    };
    init();

    return () => {
      const socket = socketRef.current;
      if (!socket) return;
      socket.off("connect_error", handleErrors);
      socket.off("connect_failed", handleErrors);
      socket.off(ACTIONS.JOIN_ERROR);
      socket.off(ACTIONS.JOINED);
      socket.off(ACTIONS.DISCONNECTED);
      socket.off(ACTIONS.ROLE_UPDATED);
      socket.off(ACTIONS.KICKED);
      socket.off(ACTIONS.BANNED);
      socket.off(ACTIONS.BANNED_USERS);
      socket.disconnect();
      socketRef.current = null;
      setIsSocketReady(false);
    };
  }, [navigate, roomId, username]);

  const copyRoomId = async () => {
    try {
      await navigator.clipboard.writeText(roomId);
      toast.success(`Room ID is copied`);
    } catch (error) {
      console.log(error);
      toast.error("Unable to copy the room ID");
    }
  };

  const leaveRoom = async () => {
    navigate("/");
  };

  const openBannedUsers = () => {
    if (!socketRef.current || myRole !== "owner") return;
    socketRef.current.emit(ACTIONS.GET_BANNED_USERS, { roomId });
    setShowBannedUsers(true);
  };

  const unbanUser = (username) => {
    if (!socketRef.current || myRole !== "owner") return;
    socketRef.current.emit(ACTIONS.UNBAN_USER, {
      roomId,
      username,
    });
  };

  const ownerClients = clients.filter(
    (client) => client.socketId === ownerId || client.role === "owner"
  );
  const editorClients = clients.filter((client) => client.role === "editor");
  const readOnlyClients = clients.filter(
    (client) =>
      !ownerClients.some((owner) => owner.socketId === client.socketId) &&
      !editorClients.some((editor) => editor.socketId === client.socketId)
  );

  const changeRole = (targetId, role) => {
    if (!socketRef.current || !roomId || myRole !== "owner") return;
    socketRef.current.emit(ACTIONS.CHANGE_ROLE, {
      roomId,
      targetId,
      role,
    });
    setActiveMenu(null);
  };

  const kickUser = (targetId, username) => {
    if (!socketRef.current || !roomId || myRole !== "owner") return;
    if (!window.confirm(`Kick ${username} from this room?`)) return;
    socketRef.current.emit(ACTIONS.KICK_USER, {
      roomId,
      targetId,
    });
    setActiveMenu(null);
  };

  const banUser = (targetId, username) => {
    if (!socketRef.current || !roomId || myRole !== "owner") return;
    if (!window.confirm(`Ban ${username} from this room?`)) return;
    socketRef.current.emit(ACTIONS.BAN_USER, {
      roomId,
      targetId,
    });
    setActiveMenu(null);
  };

  const toggleMemberMenu = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = 200;
    const x = Math.min(rect.right + 8, window.innerWidth - menuWidth - 8);
    const y = Math.min(rect.top, window.innerHeight - menuHeight - 8);

    setActiveMenu((prev) =>
      prev && prev.targetId === targetId ? null : { targetId, x, y }
    );
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!activeMenu) return;
      const clickedTrigger = e.target.closest("[data-member-menu-trigger='true']");
      if (clickedTrigger) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenu(null);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeMenu]);

  useEffect(() => {
    if (!activeMenu) return;
    const exists = clients.some((client) => client.socketId === activeMenu.targetId);
    if (!exists) setActiveMenu(null);
  }, [clients, activeMenu]);

  useEffect(() => {
    if (myRole !== "owner" && showBannedUsers) {
      setShowBannedUsers(false);
      setBannedUsers([]);
    }
  }, [myRole, showBannedUsers]);

  const renderMemberCard = (client) => (
    <div key={client.socketId} className="mb-2 member-row">
      <div className="d-flex align-items-center justify-content-between gap-2 px-1 py-1 rounded">
        <Client username={client.username} className="flex-grow-1" />
        {myRole === "owner" && client.socketId !== socketRef.current?.id ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center flex-shrink-0"
            data-member-menu-trigger="true"
            onClick={(e) => toggleMemberMenu(e, client.socketId)}
            aria-label="Member actions"
            title="Member actions"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v.5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-.5Zm0 4A1.5 1.5 0 0 1 3.5 6h9A1.5 1.5 0 0 1 14 7.5v.5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-.5Zm0 4A1.5 1.5 0 0 1 3.5 10h9a1.5 1.5 0 0 1 1.5 1.5v.5a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-.5Z" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );

  const activeMenuClient = activeMenu
    ? clients.find((client) => client.socketId === activeMenu.targetId)
    : null;

  const runCode = async () => {
    setIsCompiling(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/compile`, {
        code: codeRef.current,
        language: selectedLanguage,
      });
      console.log("Backend response:", response.data);
      setOutput(response.data.output || JSON.stringify(response.data));
    } catch (error) {
      console.error("Error compiling code:", error);
      setOutput(error.response?.data?.error || "An error occurred");
    } finally {
      setIsCompiling(false);
    }
  };
 
  const toggleCompileWindow = () => {
    setIsCompileWindowOpen(!isCompileWindowOpen);
  };

  useEffect(() => {
    const updateCompilerBounds = () => {
      if (!editorPaneRef.current) return;
      const rect = editorPaneRef.current.getBoundingClientRect();
      setCompilerBounds({
        left: Math.max(0, rect.left),
        right: Math.max(0, window.innerWidth - rect.right),
      });
    };

    updateCompilerBounds();
    const rafId = window.requestAnimationFrame(updateCompilerBounds);
    window.addEventListener("resize", updateCompilerBounds);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updateCompilerBounds);
    };
  }, [isChatOpen, isCompileWindowOpen]);

  if (!location.state) {
    return <Navigate to="/" />;
  }

  return (
    <div className="container-fluid vh-100 d-flex flex-column">
      <div className="row flex-grow-1">
        {/* Client panel */}
        <div className="col-md-2 bg-dark text-light d-flex flex-column px-2">
          <img
            src="/images/Codiq.png"
            alt="Logo"
            className="img-fluid mx-auto"
            style={{ maxWidth: "150px", marginTop: "-43px" }}
          />
          <hr style={{ marginTop: "-3rem" }} />

          {/* Client list container */}
          <div className="d-flex flex-column flex-grow-1 overflow-auto member-list">
            <span className="mb-2 fw-semibold">Members</span>

            <span className="text-uppercase text-secondary small member-section-title">Owner</span>
            {ownerClients.map((client) => renderMemberCard(client))}

            <span className="text-uppercase text-secondary small member-section-title">Editors</span>
            {editorClients.length === 0 ? (
              <small className="text-secondary mb-2 px-1">No editors</small>
            ) : (
              editorClients.map((client) => renderMemberCard(client))
            )}

            <span className="text-uppercase text-secondary small member-section-title">Read Only</span>
            {readOnlyClients.length === 0 ? (
              <small className="text-secondary mb-2 px-1">No read-only users</small>
            ) : (
              readOnlyClients.map((client) => renderMemberCard(client))
            )}
          </div>

          <hr />
          {/* Buttons */}
          <div className="mt-auto mb-3 d-grid gap-2">
            {myRole === "owner" ? (
              <button className="btn btn-outline-light w-100" onClick={openBannedUsers}>
                Banned Users
              </button>
            ) : null}
            <button className="btn btn-success w-100" onClick={copyRoomId}>
              Copy Room ID
            </button>
            <button className="btn btn-danger w-100" onClick={leaveRoom}>
              Leave Room
            </button>
          </div>
        </div>

        {/* Editor panel */}
        <div
          ref={editorPaneRef}
          className={`${
            isChatOpen ? "col-md-7" : "col-md-10"
          } text-light d-flex flex-column`}
        >
          {/* Language selector */}
          <div className="bg-dark p-2 d-flex justify-content-end align-items-center gap-2">
            <select
              className="form-select w-auto select-dark"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
            {/* CHAT TOGGLE BUTTON */}
            <button
              className="btn btn-outline-info btn-sm px-2"
              onClick={() => setIsChatOpen(!isChatOpen)}
              title={isChatOpen ? "Collapse chat" : "Expand chat"}
              aria-label={isChatOpen ? "Collapse chat" : "Expand chat"}
            >
              {isChatOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M2.146 2.146a.5.5 0 0 1 .708 0L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 1 1-.708-.708L7.293 8 2.146 2.854a.5.5 0 0 1 0-.708z" />
                </svg>
              ) : (
                <img src="/images/chats.svg" alt="Open chat" width="20" height="20" />
              )}
            </button>
          </div>
                    

          {isSocketReady && (
            <Editor
              key={roomId}
              socketRef={socketRef}
              roomId={roomId}
              canEdit={myRole === "owner" || myRole === "editor"}
              onCodeChange={(code) => {
                codeRef.current = code;
              }}
            />
          )}
        </div>
        {/* RIGHT CHAT PANEL */}
        <div
          className={`bg-dark text-light border-start ${
            isChatOpen ? "col-md-3 d-flex" : "d-none"
          } flex-column`}
          style={{ transition: "all 0.3s ease-in-out" }}
        >
          <Chat socketRef={socketRef} roomId={roomId} username={username} />
        </div>
      </div>

      {activeMenuClient
        ? createPortal(
            <div
              ref={menuRef}
              className="bg-dark border rounded p-2 shadow"
              style={{
                position: "fixed",
                top: `${activeMenu.y}px`,
                left: `${activeMenu.x}px`,
                minWidth: "190px",
                zIndex: 3000,
              }}
            >
              <label className="form-label small mb-1">Role</label>
              <select
                className="form-select form-select-sm mb-2 select-dark"
                value={
                  activeMenuClient.socketId === ownerId
                    ? "owner"
                    : activeMenuClient.role || "read-only"
                }
                onChange={(e) =>
                  changeRole(activeMenuClient.socketId, e.target.value)
                }
              >
                <option value="read-only">Read Only</option>
                <option value="editor">Editor</option>
                <option value="owner">Owner</option>
              </select>
              <button
                className="btn btn-sm btn-outline-danger w-100 mb-1"
                onClick={() =>
                  kickUser(activeMenuClient.socketId, activeMenuClient.username)
                }
              >
                Kick
              </button>
              <button
                className="btn btn-sm btn-danger w-100"
                onClick={() =>
                  banUser(activeMenuClient.socketId, activeMenuClient.username)
                }
              >
                Ban
              </button>
            </div>,
            document.body
          )
        : null}

      {showBannedUsers
        ? createPortal(
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{ background: "rgba(0,0,0,0.45)", zIndex: 3100 }}
              onClick={() => setShowBannedUsers(false)}
            >
              <div
                className="bg-dark border rounded p-3 text-light"
                style={{ width: "min(420px, 90vw)" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Banned Users</h6>
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={() => setShowBannedUsers(false)}
                  >
                    Close
                  </button>
                </div>
                {bannedUsers.length === 0 ? (
                  <small className="text-secondary">No banned users</small>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {bannedUsers.map((user) => (
                      <div
                        key={user.normalized}
                        className="d-flex align-items-center justify-content-between bg-secondary rounded p-2"
                      >
                        <span>{user.username}</span>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => unbanUser(user.username)}
                        >
                          Unban
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}

      {/* Compiler toggle button */}
      <button
        className="btn btn-primary position-fixed m-3"
        onClick={toggleCompileWindow}
        style={{
          zIndex: 1050,
          right: `${compilerBounds.right + 16}px`,
          bottom: isCompileWindowOpen ? "calc(30vh + 0.5rem)" : "1rem",
          transition: "right 0.3s ease-in-out, bottom 0.3s ease-in-out",
        }}
      >
        {isCompileWindowOpen ? "Close Compiler" : "Open Compiler"}
      </button>

      {/* Compiler section */}
      <div
        className={`bg-dark text-light p-3 ${
          isCompileWindowOpen ? "d-block" : "d-none"
        }`}
        style={{
          position: "fixed",
          bottom: 0,
          left: `${compilerBounds.left}px`,
          right: `${compilerBounds.right}px`,
          height: isCompileWindowOpen ? "30vh" : "0",
          transition: "height 0.3s ease-in-out, left 0.3s ease-in-out, right 0.3s ease-in-out",
          overflowY: "auto",
          zIndex: 1040,
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">Compiler Output ({selectedLanguage})</h5>
          <div>
            <button
              className="btn btn-success me-2"
              onClick={runCode}
              disabled={isCompiling}
            >
              {isCompiling ? "Compiling..." : "Run Code"}
            </button>
            <button className="btn btn-secondary" onClick={toggleCompileWindow}>
              Close
            </button>
          </div>
        </div>
        <pre className="bg-secondary p-3 rounded">
          {output || "Output will appear here after compilation"}
        </pre>
      </div>
    </div>
  );
}

export default EditorPage;


